import { motion } from "framer-motion";
import { Menu, RefreshCw, Search } from "lucide-react";
import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useCatalog } from "../contexts/CatalogContext";
import { InstallPrompt } from "./InstallPrompt";
import { LoadingOverlay } from "./LoadingOverlay";
import { Logo } from "./Logo";
import { MiniPlayer } from "./MiniPlayer";
import { CastPicker } from "./CastPicker";
import { MobileNav } from "./MobileNav";
import NavigationSidebar from "./NavigationSidebar";
import { PlaylistSwitcher } from "./PlaylistSwitcher";

export default function PageLayout({ children }: { children: ReactNode }) {
  const { refreshCatalog, loading, activePlaylist } = useCatalog();
  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.25 }}
      className="min-h-screen"
    >
      <NavigationSidebar />

      {/* Desktop shell only — renders null in a browser, where the Cast SDK
          supplies its own dialog. Anchored for the picker popover.

          The top offset uses the Window Controls Overlay environment variable
          rather than a fixed value. In the Electron shell the title bar is a
          36px strip owned by the OS: anything drawn inside it is not merely
          behind the minimise/maximise/close buttons, it is *unclickable*,
          because those pixels belong to the window chrome. In a browser
          `titlebar-area-height` resolves to 0 and this sits where it did. */}
      <div
        // Above the mini player (z-50); they were tied, so DOM order decided
        // and the cast button spent most of its life underneath.
        className="hidden lg:block fixed z-[60]"
        style={{ top: "calc(env(titlebar-area-height, 0px) + 12px)", right: "16px" }}
      >
        <CastPicker />
      </div>

      {/* Mobile top bar */}
      <header className="lg:hidden sticky top-0 z-30 bg-canvas/90 backdrop-blur-md border-b border-line px-3 py-2 flex items-center gap-2">
        <button
          onClick={() => setDrawerOpen(true)}
          className="p-2 text-text-secondary"
          aria-label="Menu"
        >
          <Menu size={20} />
        </button>
        <Link to="/livetv" className="flex items-center">
          <Logo className="h-7 w-auto" />
        </Link>
        <div className="flex-1" />
        {/* Search is a header action on phone — it left the tab bar when that
            dropped to five destinations, and the drawer is too deep for it. */}
        <Link to="/search" className="p-2 text-text-secondary" aria-label="Search">
          <Search size={18} />
        </Link>
        <button
          onClick={() => refreshCatalog(true)}
          disabled={loading || !activePlaylist}
          className="p-2 text-text-secondary disabled:opacity-30"
          aria-label="Refresh"
        >
          <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
        </button>
      </header>

      <main className="lg:ml-[240px] min-h-screen pb-24 lg:pb-8">{children}</main>

      <MobileNav />

      {drawerOpen && (
        <div
          className="lg:hidden fixed inset-0 z-50 bg-canvas/70 backdrop-blur-sm"
          onClick={() => setDrawerOpen(false)}
        >
          <motion.aside
            initial={{ x: -280 }}
            animate={{ x: 0 }}
            exit={{ x: -280 }}
            onClick={(e) => e.stopPropagation()}
            className="fixed left-0 top-0 bottom-0 w-[280px] bg-canvas border-r border-line p-4 overflow-y-auto"
          >
            <div className="mb-4">
              <Logo className="h-9 w-auto" />
            </div>
            <PlaylistSwitcher align="down" />
            <div className="mt-4 space-y-1">
              {[
                { to: "/livetv", label: "Live TV" },
                { to: "/guide", label: "Guide" },
                { to: "/movies", label: "Movies" },
                { to: "/series", label: "Series" },
                { to: "/catchup", label: "Catchup" },
                { to: "/favorites", label: "Favorites" },
                { to: "/search", label: "Search" },
                { to: "/download", label: "Get the app" },
                { to: "/settings", label: "Settings" },
              ].map((i) => (
                <Link
                  key={i.to}
                  to={i.to}
                  onClick={() => setDrawerOpen(false)}
                  className="block px-3 py-2.5 rounded-lg text-text-primary hover:bg-card-hover text-[14px] font-ui"
                >
                  {i.label}
                </Link>
              ))}
            </div>
          </motion.aside>
        </div>
      )}

      <LoadingOverlay />
      <MiniPlayer />
      <InstallPrompt />
    </motion.div>
  );
}
